import type {
    InvestorQuestionnaireDefinition,
    InvestorQuestionnaireEnvelope,
    InvestorQuestionnaireResponse,
    UserInvestorQuestionnaire,
    WarningConfirmationPayload,
    WarningConfirmationType,
} from "src/types";
import { HttpMethod } from "../constants";
import { rootApi } from "../rootApi";
import { ENTITY_WITH_TRANSLATIONS_TAG, INVESTOR_QUESTIONNAIRE_TAG, LOGGED_USER_TAG } from "../tags";
import { getCmsUrl, getCoreUrl } from "../utils";

interface InvestorQuestionnaireDefinitionResponse {
    data: InvestorQuestionnaireDefinition;
    meta: object;
}

interface UserInvestorQuestionnaireListResponse {
    data: UserInvestorQuestionnaire[];
    meta: object;
}

export const investorQuestionnaireApi = rootApi.injectEndpoints({
    endpoints: (build) => ({
        evaluateInvestorQuestionnaire: build.mutation<InvestorQuestionnaireResponse, InvestorQuestionnaireEnvelope>({
            query: (body) => ({
                url: getCoreUrl("/user/investor-questionnaire"),
                method: HttpMethod.POST,
                body,
            }),
            invalidatesTags: [LOGGED_USER_TAG, INVESTOR_QUESTIONNAIRE_TAG],
        }),
        userInvestorQuestionnaire: build.query<UserInvestorQuestionnaire | undefined, number>({
            query: (userId) => ({
                url: getCmsUrl("/api/user-investor-questionnaires"),
                method: HttpMethod.GET,
                params: {
                    filters: { user: { id: { $eq: userId } } },
                    populate: {
                        lastQuestionnaireOutcome: true,
                        questionnaireSubmissions: { populate: { warningsConfirmations: true } },
                    },
                },
            }),
            transformResponse: (response: UserInvestorQuestionnaireListResponse) => response.data[0],
            providesTags: [INVESTOR_QUESTIONNAIRE_TAG],
        }),
        saveWarningConfirmations: build.mutation<void, { documentId: string; warnings: WarningConfirmationPayload[] }>({
            query: ({ documentId, warnings }) => ({
                url: getCmsUrl(`/api/user-investor-questionnaires/${documentId}/warnings`),
                method: HttpMethod.POST,
                body: { data: warnings },
            }),
            invalidatesTags: [INVESTOR_QUESTIONNAIRE_TAG],
        }),
        markWarningDisplayed: build.mutation<void, { documentId: string; warningType: WarningConfirmationType }>({
            query: ({ documentId, warningType }) => ({
                url: getCmsUrl(`/api/user-investor-questionnaires/${documentId}/warnings/${warningType}/displayed`),
                method: HttpMethod.PATCH,
            }),
            invalidatesTags: [INVESTOR_QUESTIONNAIRE_TAG],
        }),
        markWarningConfirmed: build.mutation<void, { documentId: string; warningType: WarningConfirmationType }>({
            query: ({ documentId, warningType }) => ({
                url: getCmsUrl(`/api/user-investor-questionnaires/${documentId}/warnings/${warningType}/confirmed`),
                method: HttpMethod.PATCH,
            }),
            invalidatesTags: [LOGGED_USER_TAG, INVESTOR_QUESTIONNAIRE_TAG],
        }),
        investorQuestionnaireDefinition: build.query<InvestorQuestionnaireDefinition, void>({
            query: () => ({
                url: getCmsUrl("/api/investor-questionnaire-definition"),
                method: HttpMethod.GET,
                params: {
                    populate: {
                        questions: {
                            populate: {
                                answers: true,
                            },
                        },
                    },
                },
            }),
            providesTags: [ENTITY_WITH_TRANSLATIONS_TAG],
            transformResponse: (response: InvestorQuestionnaireDefinitionResponse) => response.data,
        }),
    }),
});

export const {
    useInvestorQuestionnaireDefinitionQuery,
    useEvaluateInvestorQuestionnaireMutation,
    useUserInvestorQuestionnaireQuery,
    useLazyUserInvestorQuestionnaireQuery,
    useSaveWarningConfirmationsMutation,
    useMarkWarningDisplayedMutation,
    useMarkWarningConfirmedMutation,
} = investorQuestionnaireApi;
